import React, { useMemo } from 'react'
import HeaderMatchCard from '@sport1/news-styleguide/LivetickerHeaderMatchCard'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import getCompetitors from './utils/getCompetitors'
import DisplayAd from '@/components/Ads/AdSlot/DisplayAd'
import LivetickerHeadline from '@/components/LivetickerHeader/Headline'
import LivetickerSummary from '@/components/LivetickerSummary'
import hydrateMatchEvents from '@/components/LivetickerSummary/utils/hydrateMatchEvents'
import OddsDisclaimer from '@/components/OddsDisclaimer'
import PlaceHolderBox from '@/components/PlaceHolderBox'
import { getMatchProps } from '@/components/SportEvent/Match/utils'
import { useBetting } from '@/hooks/useBetting'
import useOddsRegistration from '@/hooks/useOddsRegistration'
import { useLivetickerMatch } from '@/utils/liveticker/LivetickerMatchProvider'
import sportIdentifiers from '@/utils/sportIdentifiers/SportIdentifiersConfig'
import { useMobile } from '@/hooks/useMobile'
import { useTracking } from '@/utils/tracking/TrackingProvider'

type Props = {
    sportIdentifier?: string
}

const LivetickerHeader = ({ sportIdentifier }: Props) => {
    const { countryInfo } = useTracking()
    const { match, matchEvents } = useLivetickerMatch()
    const { isMobile } = useMobile()
    const bettingProperties = useBetting({
        match,
        marketType: 'RESULT_1x2',
        scope: 'ticker',
        // TODO: we can not update bet-service-client because of sport1-components, maybe using S1_WIDGETS like in the App?
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        trackingCreation: 'quotenbanner_liveticker',
        ignoreMatchStatus: false,
    })
    useOddsRegistration({ matchId: match?.id, odds: bettingProperties?.odds })

    const matchProps = useMemo(
        () => (match ? getMatchProps({ match, source: 'live-ticker' }) : undefined),
        [match]
    )

    if (!match || !matchProps) {
        return sportIdentifier === sportIdentifiers.soccer.name ? (
            <PlaceHolderBox height={[374, 374, 200]} />
        ) : null
    }

    const renderAd = () => {
        const placement = isMobile ? 'topmobile' : 'banner'
        return <DisplayAd disableBottomSpace placement={placement} placementId={placement} />
    }

    return (
        <NonFlexingContainer
            marginTop={['spacing-6', 'spacing-6', 'spacing-9', 'spacing-9']}
            backgroundColor="pearl"
            testID="liveticker-header"
            marginBottom="spacing-11"
        >
            <NonFlexingContainer
                paddingX={['spacing-6', 'spacing-6', 'spacing-9', 'spacing-11']}
                paddingTop={['spacing-6', 'spacing-6', 'spacing-9', 'spacing-11']}
                paddingBottom={
                    matchEvents && matchEvents.length === 0
                        ? ['spacing-6', 'spacing-6', 'spacing-9', 'spacing-11']
                        : undefined
                }
            >
                <LivetickerHeadline match={match} />
                {renderAd()}
                <NonFlexingContainer
                    testID="liveticker-header-match-container"
                    marginTop="spacing-6"
                >
                    <HeaderMatchCard
                        {...matchProps}
                        {...getCompetitors(match, matchProps)}
                        betProps={
                            bettingProperties?.uiProperties
                                ? {
                                      ...bettingProperties.uiProperties,
                                      onPressOdd: bettingProperties.onPressOdd,
                                      onPressProvider: bettingProperties.onPressBetProvider,
                                      userCountry: countryInfo?.country,
                                  }
                                : undefined
                        }
                        hasBorder
                        userCountry={countryInfo?.country}
                    />
                </NonFlexingContainer>
                <OddsDisclaimer
                    textAlign="left"
                    fontVariant="Label-S"
                    spaceX="none"
                    marginTop="spacing-6"
                />
            </NonFlexingContainer>
            {matchEvents && matchEvents.length > 0 ? (
                <LivetickerSummary matchEvents={hydrateMatchEvents(matchEvents)} />
            ) : null}
        </NonFlexingContainer>
    )
}

export default LivetickerHeader
